import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { PORTFOLIO, getProjectBySlug } from '@/data/portfolio';
import { BEFORE_AFTER } from '@/data/testimonials';
import { BUSINESS_OUTCOMES } from '@/data/whyHallie';
import WallOfTrust from '@/components/WallOfTrust';
import { PORTFOLIO as PORTFOLIO_TID } from '@/constants/testIds';
import useSEO from '@/hooks/useSEO';

export function Portfolio() {
  useSEO({
    title: 'Portfolio & Results — Real Client Engagements',
    description:
      'Selected portfolio of operations, website, SEO and customer-experience engagements with cleaning, home services, wellness and property care businesses — together with real client testimonials shared from WhatsApp.',
    path: '/portfolio',
  });

  return (
    <div data-testid={PORTFOLIO_TID.page}>
      {/* ─────────── HERO ─────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
          <Eyebrow>Portfolio & Results</Eyebrow>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-6">
            Selected work<br/>
            <span className="font-serif-italic italic text-[#C9A24D]">and what it leaves behind.</span>
          </h1>
          <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed">
            A focused selection of recent client engagements — together with the kinds of outcomes and conversations they have produced. Each one chosen because the work was meaningful and the systems compounded over time.
          </p>
        </div>
      </section>

      {/* ─────────── PORTFOLIO LIST (text-only) ─────────── */}
      <section className="border-t border-[#E6CFC7]/70">
        {PORTFOLIO.map((p, idx) => (
          <Link
            to={`/portfolio/${p.slug}`}
            key={p.slug}
            data-testid={`${PORTFOLIO_TID.card}-${p.slug}`}
            className="block border-b border-[#E6CFC7]/70 hover:bg-[#FBF7F2] transition-colors group"
          >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-baseline">
              <div className="lg:col-span-1 num-marker">{String(idx + 1).padStart(2, '0')}</div>
              <div className="lg:col-span-7">
                <div className="eyebrow">{p.industry} — {p.year}</div>
                <h2 className="font-serif-display text-3xl lg:text-[40px] leading-[1.1] tracking-tight mt-4 group-hover:text-[#C9A24D] transition-colors">
                  {p.title}
                </h2>
                <p className="mt-4 text-[#1C1C1C]/70 leading-relaxed text-base max-w-2xl">{p.summary}</p>
              </div>
              <div className="lg:col-span-3 lg:pt-2">
                <div className="eyebrow">Outcomes</div>
                <ul className="mt-3 space-y-1.5 text-sm text-[#1C1C1C]/80">
                  {p.outcomes.slice(0, 3).map((o, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[#C9A24D] shrink-0">—</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-1 flex lg:justify-end">
                <ArrowUpRight size={26} className="text-[#1C1C1C]/40 group-hover:text-[#C9A24D] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
              </div>
            </div>
          </Link>
        ))}
      </section>

      {/* ─────────── BUSINESS OUTCOMES ─────────── */}
      <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32" data-testid="portfolio-outcomes">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Business Outcomes" title="What clients walk away with" italic="when the work is done well." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E6CFC7]/60">
            {BUSINESS_OUTCOMES.map((o, i) => (
              <div key={o} className="bg-[#F4EFEA] p-8">
                <div className="num-marker">{String(i + 1).padStart(2, '0')}</div>
                <p className="mt-4 font-serif-display text-xl text-[#1C1C1C] leading-snug">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── BEFORE / AFTER ─────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Before / After" title="The shape" italic="of a typical engagement." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E6CFC7]/60">
            {BEFORE_AFTER.map((b) => (
              <div key={b.id} className="bg-[#FBF7F2] p-8 lg:p-10">
                <div className="eyebrow">{b.metric}</div>
                <p className="mt-5 text-sm uppercase tracking-wider text-[#1C1C1C]/50">Before</p>
                <p className="font-serif-display text-xl text-[#1C1C1C]/80">{b.before}</p>
                <p className="mt-5 text-sm uppercase tracking-wider text-[#C9A24D]">After</p>
                <p className="font-serif-display text-2xl text-[#1C1C1C]">{b.after}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── TESTIMONIALS = WALL OF TRUST ─────────── */}
      <WallOfTrust />

      {/* ─────────── CTA ─────────── */}
      <section className="bg-[#F4EFEA] py-24 lg:py-32 text-center border-t border-[#E6CFC7]/70">
        <div className="max-w-[1000px] mx-auto px-6">
          <Eyebrow className="mx-auto">Begin</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
            Your business<br/>
            <span className="font-serif-italic italic text-[#C9A24D]">deserves a case study like this.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="portfolio-cta-book-call">Book a Discovery Call</Link>
            <Link to="/contact" className="btn-ghost" data-testid="portfolio-cta-consult">Request a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────── DETAIL PAGE — text-only (no images) ───────────
export function PortfolioDetail() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  useSEO({
    title: project ? `${project.title}` : 'Case Study',
    description: project?.summary,
    path: `/portfolio/${slug}`,
  });

  if (!project) return <Navigate to="/portfolio" replace />;

  return (
    <div data-testid={PORTFOLIO_TID.detail}>
      <section className="py-20 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>{project.industry} &middot; {project.year}</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] tracking-tight mt-6">{project.title}</h1>
          <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed max-w-2xl">{project.summary}</p>
        </div>
      </section>

      <section className="py-12 lg:py-16 border-t border-[#E6CFC7]/70">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start space-y-8">
            <div>
              <div className="eyebrow">Client</div>
              <p className="mt-2 font-serif-display text-2xl">{project.client}</p>
            </div>
            <div>
              <div className="eyebrow">Industry</div>
              <p className="mt-2">{project.industry}</p>
            </div>
            <div>
              <div className="eyebrow">Year</div>
              <p className="mt-2">{project.year}</p>
            </div>
            <div>
              <div className="eyebrow">Tools Used</div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {project.tools.map((t) => (
                  <li key={t} className="text-xs uppercase tracking-wider bg-[#E6CFC7]/50 px-3 py-1.5">{t}</li>
                ))}
              </ul>
            </div>
          </aside>

          <article className="lg:col-span-8 space-y-14">
            <div>
              <div className="eyebrow">Challenge</div>
              <p className="mt-5 text-lg leading-relaxed text-[#1C1C1C]/85">{project.challenge}</p>
            </div>
            <div>
              <div className="eyebrow">Solution</div>
              <p className="mt-5 text-lg leading-relaxed text-[#1C1C1C]/85">{project.solution}</p>
            </div>
            <div>
              <div className="eyebrow">Outcomes</div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E6CFC7]/60">
                {project.outcomes.map((o, i) => (
                  <li key={i} className="bg-[#FBF7F2] p-6 lg:p-8">
                    <div className="num-marker">{String(i + 1).padStart(2, '0')}</div>
                    <p className="font-serif-display text-xl mt-3 text-[#1C1C1C] leading-snug">{o}</p>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32 text-center">
        <div className="max-w-[1000px] mx-auto px-6">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Want a system like this<br/>
            <span className="font-serif-italic italic text-[#C9A24D]">inside your business?</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-ghost-light" data-testid="portfolio-detail-cta-book-call">Book a Discovery Call</Link>
            <Link to="/portfolio" className="text-sm uppercase tracking-[0.18em] text-[#F4EFEA]/70 hover:text-[#C9A24D] self-center transition-colors">All projects &rarr;</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
